import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, DependencyList } from 'react';
import { useId } from 'react-use-id-hook';

import { useSession } from './session';
import Websocket, { Listener, Events, Event, Message } from '../lib/websocket';

type WebsocketContext = {
    socket: Websocket;
    isConnected: boolean;
    lostConnection: boolean;
    listen<T = any>(id: string, listener: Listener<T>): void;
    unbind: (id: string) => void;
    sendMessage: (meta: string, data: any) => void;
    on: (event: Events, callback: Event['callback']) => void;
    off: (event: Events, callback?: Event['callback']) => void;
};

/** Context */
const WebsocketsContext = createContext<WebsocketContext|null>(null);

/** Hook */
export function useWebsockets() {
    return useContext(WebsocketsContext) as WebsocketContext;
}

/** Provider */
export function WebsocketsProvider(props: { children: React.ReactNode }) {
    const session = useSession();
    const [socket, setSocket] = useState(new Websocket(session.id));
    const [isConnected, setIsConnected] = useState(false);
    const [lostConnection, setLostConnection] = useState(false);

    useEffect(() => {
        const newSocket = new Websocket(session.id);

        setSocket(newSocket);

        const onDisconnect = () => {
            setLostConnection(true);
        };

        newSocket.on('connect', () => {
            setIsConnected(true);
            setLostConnection(false);

            const existingToken = document.getElementById('chat-app');

            if(existingToken) {
                const token = existingToken.getAttribute('data-auth-token') || '';

                if(token) {
                    newSocket.sendMessage('auth', existingToken.getAttribute('data-auth-token') || '');
                }
            }
        });

        newSocket.on('disconnect', onDisconnect);
        newSocket.connect();

        const onAuth = (event: any) => {
            newSocket.sendMessage('auth', event.detail);
        };

        window.addEventListener('chat.authenticate', onAuth);

        return () => {
            newSocket.off('disconnect', onDisconnect);
            newSocket.disconnect();
            window.removeEventListener('chat.authenticate', onAuth);
        };
    }, [session.id]);

    const listen = useCallback(<T = any>(id: string, listener: Listener<T>) => {
        socket.listen(id, listener);
    }, [socket]);

    const unbind = useCallback((id: string) => {
        socket.unbind(id);
    }, [socket]);

    const sendMessage = useCallback((meta: string, data: any) => {
        socket.sendMessage(meta, data);
    }, [socket]);

    const on = useCallback((event: Events, callback: Event['callback']) => {
        socket.on(event, callback);
    }, [socket]);

    const off = useCallback((event: Events, callback?: Event['callback']) => {
        socket.off(event, callback);
    }, [socket]);

    const value = useMemo(() => ({
        socket,
        isConnected,
        lostConnection,
        listen,
        unbind,
        sendMessage,
        on,
        off,
    }), [socket, isConnected, lostConnection, listen, unbind, sendMessage, on, off]);

    return <>
        {isConnected && <WebsocketsContext.Provider value={value}>
            {props.children}
        </WebsocketsContext.Provider>}
    </>;
}

export function useListen<T = any>(event: string, listener: (data: T) => void, deps?: DependencyList) {
    const ws = useWebsockets();
    const id = useId();

    useEffect(() => {
        const callback = (message: Message<T>) => {
            if(message.meta === event) {
                listener(message.data);
            }
        };

        ws.listen(id, callback);

        return () => {
            ws.unbind(id);
        };
    }, [ws.socket, ws.lostConnection, event, ...(deps || [])]);
}
